import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaymentEventDef } from '@rb/shared/master-data/types';
import type { RewardOption } from '@/components/admin/mail/reward-options';
import { getMailRewardOptions } from '@/client/admin.client';
import { Button } from '@/components/ui/button';
import { ApplyPanel } from '@/master-data/ApplyPanel';
import { EnvSelector, type EnvSchema } from '@/master-data/EnvSelector';
import {
  deletePaymentEvent,
  insertPaymentEvent,
  listPaymentEvents,
  setActivePaymentEvent,
  updatePaymentEvent,
} from './payment-event.crud';
import {
  cloneDraftFromRow,
  draftFromRow,
  draftToInsertValues,
  draftToValues,
  emptyDraft,
  nextEventId,
  validatePaymentEvent,
  type PaymentEventDraft,
} from './payment-event.model';
import { PaymentEventForm } from './PaymentEventForm';
import { PaymentEventRow } from './PaymentEventRow';

/** production 쓰기는 형제 패널들과 같은 관례로 확인 다이얼로그를 한 번 더 받는다. */
function confirmProdWrite(env: EnvSchema, action: string): boolean {
  if (env !== 'production') return true;
  return window.confirm(`production 스키마의 결제 이벤트를 ${action}합니다. 계속하시겠습니까?`);
}

export function PaymentEventPanel() {
  const [env, setEnv] = useState<EnvSchema>('staging');
  const [rows, setRows] = useState<PaymentEventDef[]>([]);
  const [options, setOptions] = useState<RewardOption[]>([]);
  const [draft, setDraft] = useState<PaymentEventDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);

  // env 를 빠르게 전환하면 이전 env 의 응답이 나중에 도착해 현재 화면을 덮을 수 있다.
  // 그 상태에서 라디오를 누르면 다른 env 의 id 들로 update 가 나가고 crud EF 는 매칭
  // pk 가 없어도 200 을 주므로 조용히 어긋난다. 세대 카운터로 stale 응답을 버린다.
  const requestSeq = useRef(0);
  // 열려 있는 draft 를 fetch 콜백에서 참조하기 위한 미러 — fetchRows 의 deps 에
  // draft 를 넣으면 reload useEffect 가 매 편집마다 재실행된다.
  const openDraftRef = useRef<{ id: number; isNew: boolean } | null>(null);
  useEffect(() => {
    openDraftRef.current = draft ? { id: draft.id, isNew } : null;
  }, [draft, isNew]);

  // 목록만 다시 받아온다 — 에러 배너 상태는 건드리지 않는다. reload() 는 이걸 감싸
  // 로딩 표시 + 에러 초기화까지 하지만, run() 의 실패 경로에서는 방금 세운 에러
  // 메시지를 지우면 안 되므로 이 함수를 직접 쓴다.
  const fetchRows = useCallback(async () => {
    const seq = ++requestSeq.current;
    const next = await listPaymentEvents(env);
    // 더 최신 요청이 이미 떠났으면(= env 가 바뀌었으면) 이 응답은 버린다.
    if (seq !== requestSeq.current) return;
    setRows(next);

    // 폼을 연 채 같은 행이 사라지면(다른 작업자의 삭제 또는 본인 삭제) 저장 시
    // 존재하지 않는 pk 로 update 가 나가고 crud EF 는 에러로 보지 않아 "저장됨"처럼
    // 보이지만 아무것도 저장되지 않는다. 그 전에 폼을 닫고 알린다.
    const open = openDraftRef.current;
    if (open && !open.isNew && !next.some((r) => r.id === open.id)) {
      setDraft(null);
      openDraftRef.current = null;
      setNotice(
        `편집 중이던 이벤트 #${open.id} 가 목록에 없어 편집을 닫았습니다. 삭제됐거나 다른 작업자가 지웠을 수 있습니다.`
      );
    }
  }, [env]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await fetchRows();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [fetchRows]);

  useEffect(() => {
    setDraft(null);
    void reload();
  }, [reload]);

  useEffect(() => {
    getMailRewardOptions()
      .then(setOptions)
      // 보상 옵션은 best-effort — 실패해도 직접 입력으로 편집할 수 있다.
      .catch((e: unknown) => setError(`보상 옵션 로드 실패: ${String(e)}`));
  }, []);

  // 신규면 전체 행을 대상으로 id 중복만 검사하고, 편집이면 자기 자신을 제외한
  // 나머지 행을 대상으로 검사한다.
  const original = draft && !isNew ? rows.find((r) => r.id === draft.id) : undefined;
  const others = draft && !isNew ? rows.filter((r) => r.id !== draft.id) : rows;
  const issues = draft ? validatePaymentEvent(draft, others, { original }) : [];

  async function run(fn: () => Promise<void>) {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await fetchRows();
    } catch (e) {
      // fn() 자체가 실패했든(예: setActivePaymentEvent 가 기존 active 를 내린 뒤
      // 대상을 올리는 중간에 죽어 DB 가 active 0개가 되는 경우), fn() 은 성공했지만
      // 이어진 fetchRows() 가 실패했든 — 어느 쪽이든 화면의 stale rows 가 checked=
      // {row.active} 로 예전 상태를 계속 보여주면 운영자가 "실패했으니 원래대로겠지"
      // 로 오판하고 그대로 Apply 할 위험이 있다. 그래서 실패 시에도 반드시 한 번 더
      // 재조회를 시도한다. reload() 를 쓰면 그 안의 setError(null) 이 지금 세우려는
      // 에러 배너를 지워버리므로 fetchRows 를 직접 호출하고, 재조회 자체가 또
      // 실패해도 원래 에러 메시지가 화면에 남도록 setError 는 재조회 뒤에 한다.
      const message = (e as Error).message;
      try {
        await fetchRows();
      } catch {
        // 재조회도 실패하면 rows 는 이전 상태로 남지만, 최소한 에러 메시지는
        // 방금 실패의 것을 유지한다.
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  const onSave = () => {
    if (!draft) return;
    // 폼의 저장 버튼 비활성화(issues.length > 0)와 별개로 여기서도 게이트를 둔다.
    // draftToValues 는 날짜 파싱 실패 시 start_at/end_at 을 0 으로 폴백하는데,
    // end_at=0 은 서버 클레임 마감 컷을 즉시 마감으로 만드는 위험한 값이라
    // 검증을 통과하지 못한 draft 가 저장 경로를 타는 일을 이중으로 막는다.
    if (issues.length > 0) return;
    if (!confirmProdWrite(env, isNew ? '새로 생성' : `#${draft.id} 수정`)) return;
    void run(async () => {
      // 편집 저장에는 active 가 들어가지 않는다(활성 전환은 라디오 전담), 신규는
      // 항상 비활성으로 만든다.
      if (isNew) await insertPaymentEvent(env, draftToInsertValues(draft));
      else await updatePaymentEvent(env, draft.id, draftToValues(draft));
      setDraft(null);
    });
  };

  const onActivate = (row: PaymentEventDef) => {
    if (!confirmProdWrite(env, `활성 이벤트를 #${row.id} 로 전환`)) return;
    void run(() => setActivePaymentEvent(env, row.id, rows));
  };

  const onDelete = (row: PaymentEventDef) => {
    if (!window.confirm(`이벤트 #${row.id} 를 삭제합니다. 계속할까요?`)) return;
    if (!confirmProdWrite(env, `#${row.id} 삭제`)) return;
    void run(() => deletePaymentEvent(env, row.id));
  };

  return (
    <section className="flex flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold">결제 이벤트</h1>
        <p className="text-xs text-muted-foreground">
          누적 결제 VX 구간별 보상. active 행은 항상 1개만 유지되며, 활성 전환은 즉시 DB 에
          반영되지만 게임에는 Apply 이후에 나갑니다.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <EnvSelector value={env} onChange={setEnv} />
        </div>
        <Button type="button" variant="outline" onClick={() => setApplyOpen(true)}>
          Apply ({env})
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setIsNew(true);
            setDraft(emptyDraft(nextEventId(rows), Date.now()));
          }}
        >
          신규 이벤트
        </Button>
      </div>

      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
        Apply 는 결제 이벤트만이 아니라 이 env 의 마스터데이터 전체가 함께 발행됩니다. 다른 작업자가
        편집 중인 행이 없는지 확인하세요.
      </p>

      {error && (
        <p className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-400">
          {error}
        </p>
      )}

      {notice && (
        <p className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-700 dark:text-sky-400">
          {notice}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <PaymentEventRow
              key={row.id}
              row={row}
              saving={saving}
              onActivate={() => onActivate(row)}
              onEdit={() => {
                setIsNew(false);
                setDraft(draftFromRow(row));
              }}
              onClone={() => {
                setIsNew(true);
                setDraft(cloneDraftFromRow(row, nextEventId(rows)));
              }}
              onDelete={() => onDelete(row)}
            />
          ))}
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              이 env 에 등록된 결제 이벤트가 없습니다.
            </p>
          )}
        </div>
      )}

      {draft && (
        <PaymentEventForm
          draft={draft}
          options={options}
          issues={issues}
          saving={saving}
          onChange={setDraft}
          onSave={onSave}
          onCancel={() => setDraft(null)}
        />
      )}

      {applyOpen && <ApplyPanel env={env} onClose={() => setApplyOpen(false)} />}
    </section>
  );
}
